import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import Error from '../../../components/Error';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';

/**
 * Props:
 * - item?: { _id, album, name, host, music, czechLyrics }
 * - albums: Array<{ _id, name }>
 * - onItemsUpdate?: () => void
 * - close?: () => void
 */
const DiscographyItemModal = ({ item, albums = [], onItemsUpdate, close }) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const isEdit = Boolean(item?._id);

    // form state
    const [albumOpt, setAlbumOpt] = useState(null); // { value, label } | null
    const [name, setName] = useState(item?.name || '');
    const [host, setHost] = useState(item?.host || '');
    const [music, setMusic] = useState(item?.music || '');
    const [czechLyrics, setCzechLyrics] = useState(item?.czechLyrics || '');
    const [error, setError] = useState('');

    useEffect(() => { setTimeout(() => inputRef.current?.focus(), 1); }, []);

    useEffect(() => {
        // re-init při otevření s jiným řádkem
        const albumId = typeof item?.album === 'object' && item?.album?._id ? item.album._id : item?.album;
        const found = (albums || []).find(a => String(a._id) === String(albumId));
        setAlbumOpt(found ? { value: found._id, label: found.name } : null);

        setName(item?.name || '');
        setHost(item?.host || '');
        setMusic(item?.music || '');
        setCzechLyrics(item?.czechLyrics || '');
        setError('');
    }, [item, albums]);

    const errors = useMemo(() => {
        const e = {};
        if (!name.trim()) e.name = t('validation.required');
        return e;
    }, [name, t]);

    const albumOptions = useMemo(() => (albums || []).map(a => ({ value: a._id, label: a.name })), [albums]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (Object.keys(errors).length) return;

        const payload = {
            album: albumOpt?.value || null,
            name: name.trim(),
            host: host?.trim() || undefined,
            music: music?.trim() || undefined,
            czechLyrics: czechLyrics?.trim() || undefined
        };

        try {
            if (isEdit) {
                await API.put(API_ENDPOINTS.discographyItems.update(item._id), payload);
            } else {
                await API.post(API_ENDPOINTS.discographyItems.create, payload);
            }
            onItemsUpdate?.();
            close?.();
        } catch (err) {
            console.error('Failed to upsert discography item:', err);
            setError(err);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" className="mb-2">
                {isEdit ? t('discography.editItem') : t('discography.addItem')}
            </Typography>

            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Select
                    label={t('discography.album')}
                    value={albumOpt}
                    options={albumOptions}
                    onChange={setAlbumOpt}
                    isClearable
                />

                <Forms.Text
                    ref={inputRef}
                    required
                    label={t('discography.itemName')}
                    placeholder={t('discography.itemName')}
                    value={name}
                    onChange={setName}
                    error={errors.name}
                    className="rounded-md"
                />

                <Forms.Text
                    label={t('discography.host')}
                    placeholder={t('discography.host')}
                    value={host}
                    onChange={setHost}
                    className="rounded-md"
                />

                <Forms.Text
                    label={t('discography.music')}
                    placeholder={t('discography.music')}
                    value={music}
                    onChange={setMusic}
                    className="rounded-md"
                />

                {/* Text delšího rozsahu – pokud nemáš Forms.Textarea, klidně nahraď nativním <textarea>. */}
                <div className="col-span-2 flex flex-col gap-1">
                    <label className="font-medium">{t('discography.czechLyrics')}</label>
                    <Forms.Text
                        value={czechLyrics}
                        onChange={setCzechLyrics}
                        rows={6}
                        placeholder={t('discography.czechLyrics')}
                    />
                </div>
            </div>

            {error && <Error error={error} />}

            <Forms.Button submit label={t('save')} className="mt-4" />
        </form>
    );
};

export default DiscographyItemModal;
